import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useLazyQuery } from '@apollo/client';
import { getModifyDrawerSections } from '@common/constants';
import {
  useGetActivePersonsList,
  useGetActivePersonsWithoutBusinessPartnersList,
  useGetBusinessPartnerTypeEnumList,
  useGetCountriesList,
  useGetCurrenciesList,
  useGetRelatedCompaniesList,
  useGetTitlesList,
  useGetZonesList,
  useUpdateBusinessPartner
} from '@common/slices';
import { IModifyBusinessPartner } from '@common/types';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { IAbstractRecord } from '@models';
import { searchActiveBusinessPartnersQuery } from '@modules/salesforce/business-partners/business-partners-listing/salesforce-business-partners-drawer/queries';
import { businessPartnerLookup } from '@utils';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';

interface ModifyBusinessPartnerDrawerProps {
  open: boolean;
  onClose: () => void;
  businessPartnerId: string;
  businessPartnerDetails: IModifyBusinessPartner;
}

const ModifyBusinessPartnerDrawer: React.FC<
  ModifyBusinessPartnerDrawerProps
> = ({ open, onClose, businessPartnerId, businessPartnerDetails }) => {
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);
  const [values, setValues] = useState<IAbstractRecord>({});
  const [booted, setBooted] = useState(false);

  const { data: activePersons } = useGetActivePersonsList();
  const { data: personsWithoutBusinessPartners } =
    useGetActivePersonsWithoutBusinessPartnersList();
  const { data: companies } = useGetRelatedCompaniesList();
  const { data: titles } = useGetTitlesList();
  const { data: countries } = useGetCountriesList();
  const { data: zones } = useGetZonesList();
  const { data: currencies } = useGetCurrenciesList();
  const { data: businessPartnerTypes } = useGetBusinessPartnerTypeEnumList();

  const [updateBusinessPartner] = useUpdateBusinessPartner(businessPartnerId);
  const [searchBusinessPartnersLazy] = useLazyQuery(
    searchActiveBusinessPartnersQuery
  );

  const allDataLoaded =
    personsWithoutBusinessPartners?.salesforce
      ?.getActivePersonsWithoutBusinessPartners &&
    companies?.salesforce?.getRelatedCompanies &&
    titles?.core?.getTitles &&
    countries?.core?.getCountries &&
    zones?.core?.getZones &&
    currencies?.core?.getCurrencies &&
    businessPartnerTypes?.BusinessPartnerType;
  const loadLookups = useCallback(async () => {
    try {
      const activeBusinessPartnersRes = await searchBusinessPartnersLazy({
        fetchPolicy: 'network-only',
        variables: {
          keyword: '',
          pagination: { pageNumber: 1, pageSize: 50 }
        }
      });

      const lov = businessPartnerLookup({
        salesforce: {
          getActivePersons: activePersons?.salesforce?.getActivePersons,
          getActivePersonsWithoutBusinessPartners:
            personsWithoutBusinessPartners?.salesforce
              ?.getActivePersonsWithoutBusinessPartners,
          getRelatedCompanies: companies?.salesforce?.getRelatedCompanies
        },
        core: {
          getTitles: titles?.core?.getTitles,
          getCountries: countries?.core?.getCountries,
          getZones: zones?.core?.getZones,
          getCurrencies: currencies?.core?.getCurrencies
        },
        BusinessPartnerType: businessPartnerTypes?.BusinessPartnerType
      });

      const activeBusinessPartners: Record<string, string> = {};
      activeBusinessPartnersRes.data?.salesforce?.searchBusinessPartners?.items?.forEach(
        (el: any) => {
          activeBusinessPartners[el.id] = `${el.code} - ${el.fullName}`;
        }
      );

      const lovWithBp = { ...lov, activeBusinessPartners };

      setLovs(lovWithBp);

      return lovWithBp;
    } catch (err) {
      toast.error(
        <ToastErrorMessage>Failed to load dropdowns</ToastErrorMessage>
      );

      return null;
    }
  }, [allDataLoaded]);

  const loadBusinessPartnerData = () => {
    try {
      const formatted: IAbstractRecord = {
        type: businessPartnerDetails?.type,
        title: businessPartnerDetails?.title,
        firstName: businessPartnerDetails?.firstName,
        middleName: businessPartnerDetails?.middleName,
        lastName: businessPartnerDetails?.lastName,
        dateOfBirth: businessPartnerDetails?.dateOfBirth,
        companyName: businessPartnerDetails?.name,
        email: businessPartnerDetails?.email,
        mobileNumber: businessPartnerDetails?.phoneNumber,
        relatedCompany: businessPartnerDetails?.relatedCompany,
        status: businessPartnerDetails?.status,
        bpType: businessPartnerDetails?.businessPartnerType,
        isCompany: businessPartnerDetails?.isCompany,
        isLocal: businessPartnerDetails?.isLocal,
        isRegistered: businessPartnerDetails?.isRegistered,
        registerNumber: businessPartnerDetails?.registerationNumber,
        registrationExpiryDate: businessPartnerDetails?.registerationExpiryDate,
        taxAuxiliary: businessPartnerDetails?.taxAuxiliaryAccount,
        auxiliary: businessPartnerDetails?.auxiliaryAccount,
        allowAccessToAllInsured: businessPartnerDetails?.hasAccessToAllInsured
      };

      return formatted;
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);

      return {};
    }
  };

  useEffect(() => {
    if (open && allDataLoaded) {
      Promise.all([loadLookups(), loadBusinessPartnerData()]).then(
        ([lovData, businessPartnerDetails]) => {
          setValues(businessPartnerDetails);
          setSections(getModifyDrawerSections(businessPartnerDetails, lovData));
          setBooted(true);
        }
      );
    }
  }, [open, allDataLoaded, loadLookups]);

  const handleChange = (
    fieldName: string,
    value: any,
    sectionIndex: number,
    currentValues: IAbstractRecord
  ) => {
    const updated = { ...values, ...currentValues, [fieldName]: value };
    setValues(updated);
    setSections(getModifyDrawerSections(updated, lovs));
  };

  return (
    <DynamicFormDrawer
      title="Modify Business Partner"
      isOpen={open}
      onClose={onClose}
      sections={sections}
      loading={!booted}
      disableForm={false}
      onSubmit={async (formData) => {
        const mergedFormData = { ...values, ...formData };
        const isNewStatus = mergedFormData.status?.toLowerCase() === 'new';
        const payload = {
          id: businessPartnerId,
          taxAuxiliary: mergedFormData.taxAuxiliary || null,
          relatedCompany: isNewStatus
            ? mergedFormData.relatedCompany || null
            : businessPartnerDetails.relatedCompany || null,
          registrationNumber: mergedFormData.registerNumber || null,
          registrationExpiryDate: mergedFormData.registrationExpiryDate || null,
          isRegistered: mergedFormData.isRegistered,
          isLocal: mergedFormData.isLocal,
          isCompany: mergedFormData.isCompany,
          businessPartnerType: mergedFormData.bpType || null,
          auxiliary: mergedFormData.auxiliary || null,
          allowAccessToAllInsured: mergedFormData.allowAccessToAllInsured
        };

        await updateBusinessPartner({
          modifyBusinessPartnerInput: payload
        });
        onClose();
      }}
      onChange={handleChange}
      onAutocompleteSearch={(fieldName, value, pagination) => {
        if (fieldName === 'overrideBroker') {
          return searchBusinessPartnersLazy({
            fetchPolicy: 'network-only',
            variables: {
              keyword: value,
              pagination: { ...pagination, pageSize: 50 }
            }
          }).then((res) => ({
            options: lookupListAsRecordObject(
              res.data?.salesforce?.searchBusinessPartners?.items,
              true,
              'id',
              'fullName',
              'code'
            ),
            totalCount:
              res.data?.salesforce?.searchBusinessPartners?.paging
                ?.totalCount || 0
          }));
        }

        return Promise.resolve({ options: {}, totalCount: 0 });
      }}
    />
  );
};

export { ModifyBusinessPartnerDrawer };
